import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeftIcon,
  PencilSquareIcon,
  DocumentTextIcon,
  WalletIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import {
  clientService, documentService, paiementService, gdprService,
  contactService, opportuniteService, tarifService, portalAdminService, activiteService,
} from '../../api/services';
import { ConfirmDialog } from '../../components/common/ConfirmDialog';
import toast from 'react-hot-toast';
import { useLanguage } from '../../contexts/LanguageContext';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { MoneyDisplay } from '../../components/common/MoneyDisplay';
import { StatusBadge } from '../../components/common/StatusBadge';
import { formatDate, formatTND } from '../../utils/formatters';
import {
  Client, Document, Paiement, Contact, Opportunite, ETAPE_OPPORTUNITE_LABELS, ScoreClient, TarifClient,
  Activite, TypeActivite, RecommandationClient,
} from '../../types';

type Tab = 'factures' | 'devis' | 'paiements' | 'contacts' | 'opportunites' | 'tarifs' | 'activites';

const TAB_LABELS: Record<Tab, string> = {
  factures: 'Factures',
  devis: 'Devis',
  paiements: 'Paiements',
  contacts: 'Contacts',
  opportunites: 'Opportunités',
  tarifs: 'Tarifs',
  activites: 'Activités',
};

const MONTHS_FR = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];

export function ClientWorkspace() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [client, setClient] = useState<Client | null>(null);
  const [docs, setDocs] = useState<Document[]>([]);
  const [paiements, setPaiements] = useState<Paiement[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [opportunites, setOpportunites] = useState<Opportunite[]>([]);
  const [activites, setActivites] = useState<Activite[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<Tab>('factures');
  const [showAnon, setShowAnon] = useState(false);
  const [newContact, setNewContact] = useState<Partial<Contact> | null>(null);
  const [score, setScore] = useState<ScoreClient | null>(null);
  const [recommandations, setRecommandations] = useState<RecommandationClient | null>(null);
  const [tarifs, setTarifs] = useState<TarifClient[]>([]);
  const [newTarif, setNewTarif] = useState<{ type: 'PRIX_NET' | 'REMISE_POURCENTAGE'; valeur: number } | null>(null);

  const reloadTarifs = (clientId: number) =>
    tarifService.getByClient(clientId).then(setTarifs).catch(() => {});

  const saveTarif = async () => {
    if (!client || !newTarif || !newTarif.valeur) { toast.error('Valeur requise'); return; }
    try {
      await tarifService.create({ clientId: client.id, type: newTarif.type, valeur: newTarif.valeur });
      toast.success('Condition tarifaire ajoutée');
      setNewTarif(null);
      reloadTarifs(client.id);
    } catch {
      toast.error('Ajout impossible');
    }
  };

  const removeTarif = async (tarifId: number) => {
    if (!client) return;
    await tarifService.delete(tarifId);
    reloadTarifs(client.id);
  };

  const reloadContacts = (clientId: number) =>
    contactService.getByClient(clientId).then(setContacts).catch(() => {});

  const saveContact = async () => {
    if (!client || !newContact?.nom) { toast.error('Le nom est obligatoire'); return; }
    try {
      await contactService.create({ ...newContact, clientId: client.id });
      toast.success('Contact ajouté');
      setNewContact(null);
      reloadContacts(client.id);
    } catch {
      toast.error('Ajout impossible');
    }
  };

  const removeContact = async (contactId: number) => {
    if (!client) return;
    await contactService.delete(contactId);
    reloadContacts(client.id);
  };

  const anonymize = async () => {
    if (!client) return;
    try {
      await gdprService.anonymizeClient(client.id);
      toast.success('Client anonymisé (RGPD)');
      navigate('/clients');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Anonymisation impossible');
    } finally {
      setShowAnon(false);
    }
  };

  useEffect(() => {
    if (!id) return;
    (async () => {
      setLoading(true);
      try {
        const clientId = Number(id);
        const [c, allDocs, allPaiements, cts, opps] = await Promise.all([
          clientService.getById(clientId),
          documentService.getAll({ size: 1000, clientId } as any).then((p) => p.content),
          paiementService.getAll({ size: 1000 } as any).then((p) => p.content),
          contactService.getByClient(clientId).catch(() => []),
          opportuniteService.getByClient(clientId).catch(() => []),
        ]);
        setClient(c);
        setDocs(allDocs.filter((d: any) => d.clientId === clientId));
        setContacts(cts);
        setOpportunites(opps);
        clientService.getScore(clientId).then(setScore).catch(() => {});
        clientService.getRecommandations(clientId).then(setRecommandations).catch(() => {});
        tarifService.getByClient(clientId).then(setTarifs).catch(() => {});
        activiteService.getByClient(clientId).then(setActivites).catch(() => {});
        setPaiements(allPaiements.filter((p: any) => {
          const linked = allDocs.find((d: any) => d.id === p.documentId);
          return linked && linked.clientId === clientId;
        }));
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const factures = useMemo(() => docs.filter((d: any) => d.typeDocument === 'FACTURE'), [docs]);
  const devis = useMemo(() => docs.filter((d: any) => d.typeDocument === 'DEVIS'), [docs]);

  const stats = useMemo(() => {
    const totalCA = factures.reduce((s: number, d: any) => s + Number(d.totalHT || 0), 0);
    const encours = factures
      .filter((d: any) => d.statutPaiement !== 'PAYEE' && d.statutPaiement !== 'ANNULEE')
      .reduce((s: number, d: any) => s + Number(d.netAPayer || 0) - Number(d.montantPaye || 0), 0);
    const today = new Date();
    const overdueDocs = factures.filter((d: any) => {
      if (!d.dateEcheance || d.statutPaiement === 'PAYEE') return false;
      return new Date(d.dateEcheance) < today;
    });
    const totalCo2e = factures.reduce((s: number, d: any) => s + Number(d.co2eKg || 0), 0);
    return {
      totalCA,
      encours,
      overdueCount: overdueDocs.length,
      totalCo2e,
      facturesCount: factures.length,
    };
  }, [factures]);

  const monthlyChart = useMemo(() => {
    const m = new Map<string, number>();
    const now = new Date();
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      m.set(key, 0);
    }
    for (const f of factures as any[]) {
      const dd = new Date(f.dateDocument);
      const key = `${dd.getFullYear()}-${String(dd.getMonth() + 1).padStart(2, '0')}`;
      if (m.has(key)) m.set(key, (m.get(key) || 0) + Number(f.totalHT || 0));
    }
    return Array.from(m.entries()).map(([k, v]) => {
      const [, mo] = k.split('-');
      return { mois: MONTHS_FR[Number(mo) - 1], montant: v };
    });
  }, [factures]);

  if (loading) return <LoadingSpinner />;
  if (!client) {
    return (
      <div className="card text-center text-gray-500 py-12">
        Client introuvable. <button className="text-primary-600 underline" onClick={() => navigate('/clients')}>Retour</button>
      </div>
    );
  }

  const listByTab: any[] =
    tab === 'factures' ? factures :
    tab === 'devis' ? devis :
    paiements;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start gap-4">
        <button onClick={() => navigate('/clients')} className="btn-outline p-2">
          <ArrowLeftIcon className="h-5 w-5" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="page-title truncate">{client.raisonSociale}</h1>
            {score && (
              <span
                title={`Score ${score.score}/100 · ${score.nbEnRetard} en retard${score.depassementPlafond ? ' · plafond dépassé' : ''}`}
                className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                  score.categorie === 'A' ? 'bg-green-100 text-green-800'
                    : score.categorie === 'B' ? 'bg-amber-100 text-amber-800'
                    : 'bg-red-100 text-red-800'
                }`}
              >
                Score {score.score} · {score.categorie}
              </span>
            )}
          </div>
          <div className="text-sm text-gray-500 mt-1 flex flex-wrap gap-x-4 gap-y-1">
            {client.matriculeFiscal && <span>MF: {client.matriculeFiscal}</span>}
            {client.code && <span>Code: {client.code}</span>}
            {client.ville && <span>{client.ville}</span>}
            {client.tauxRetenueSource ? <span>RS: {client.tauxRetenueSource}%</span> : null}
          </div>
        </div>
        <button onClick={() => navigate(`/clients/${client.id}/edit`)} className="btn-outline">
          <PencilSquareIcon className="h-4 w-4 mr-1" />
          {t.common.edit}
        </button>
        <button onClick={() => setShowAnon(true)} className="btn-outline text-red-600 border-red-200 hover:bg-red-50">
          Anonymiser (RGPD)
        </button>
        <button
          onClick={async () => {
            const info = await portalAdminService.generateToken(client.id);
            const url = `${window.location.origin}/portail/${info.token}`;
            try { await navigator.clipboard?.writeText(url); } catch { /* clipboard indisponible */ }
            toast.success(`Lien portail copié : ${url}`);
          }}
          className="btn-outline"
        >
          Lien portail
        </button>
        <button
          onClick={() => navigate(`/documents/new?type=FACTURE&clientId=${client.id}`)}
          className="btn-primary"
        >
          <DocumentTextIcon className="h-4 w-4 mr-1" />
          Nouvelle facture
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard label="CA cumulé" value={formatTND(stats.totalCA)} icon={WalletIcon} accent="text-emerald-600" />
        <KpiCard label="Encours" value={formatTND(stats.encours)} icon={ClockIcon} accent={stats.encours > 0 ? 'text-amber-600' : 'text-gray-500'} />
        <KpiCard
          label="Factures en retard"
          value={String(stats.overdueCount)}
          icon={DocumentTextIcon}
          accent={stats.overdueCount > 0 ? 'text-red-600' : 'text-gray-500'}
        />
        <KpiCard
          label="Empreinte carbone"
          value={`${stats.totalCo2e.toLocaleString('fr-TN', { maximumFractionDigits: 1 })} kg CO₂e`}
          icon={DocumentTextIcon}
          accent="text-emerald-700"
        />
      </div>

      {recommandations && (
        <div className="card border-l-4 border-l-emerald-400">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-semibold text-gray-700">Recommandations commerciales</h2>
            <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
              {recommandations.segment} · potentiel {recommandations.potentiel.toLowerCase()}
            </span>
          </div>
          <ul className="space-y-1 text-sm text-gray-700 list-disc pl-5">
            {recommandations.actions.map((a, i) => <li key={i}>{a}</li>)}
          </ul>
        </div>
      )}

      <div className="card">
        <h2 className="text-sm font-semibold text-gray-700 mb-3">Chiffre d'affaires — 12 derniers mois</h2>
        <div style={{ width: '100%', height: 240 }}>
          <ResponsiveContainer>
            <BarChart data={monthlyChart} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
              <CartesianGrid stroke="#f3f4f6" vertical={false} />
              <XAxis dataKey="mois" tick={{ fontSize: 11 }} stroke="#9ca3af" />
              <YAxis tick={{ fontSize: 11 }} stroke="#9ca3af" tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v: any) => formatTND(Number(v))} />
              <Bar dataKey="montant" fill="#10b981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="card overflow-hidden p-0">
        <div className="flex border-b border-gray-100">
          {(Object.keys(TAB_LABELS) as Tab[]).map((tt) => (
            <button
              key={tt}
              type="button"
              onClick={() => setTab(tt)}
              className={`px-4 py-3 text-sm font-medium border-b-2 -mb-px transition ${
                tab === tt
                  ? 'border-emerald-500 text-emerald-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {TAB_LABELS[tt]}
              <span className="ml-2 text-xs text-gray-400">
                ({tt === 'factures' ? factures.length
                  : tt === 'devis' ? devis.length
                  : tt === 'paiements' ? paiements.length
                  : tt === 'contacts' ? contacts.length
                  : tt === 'tarifs' ? tarifs.length
                  : tt === 'activites' ? activites.length
                  : opportunites.length})
              </span>
            </button>
          ))}
        </div>
        <div className="overflow-x-auto">
          {tab === 'activites' ? (
            <ActivitesTab
              clientId={client.id}
              activites={activites}
              onChange={() => activiteService.getByClient(client.id).then(setActivites).catch(() => {})}
            />
          ) : tab === 'tarifs' ? (
            <div className="p-4 space-y-3">
              {newTarif ? (
                <div className="flex flex-wrap items-end gap-2 border rounded-md p-3 bg-gray-50">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Type</label>
                    <select className="input-field" value={newTarif.type}
                            onChange={(e) => setNewTarif({ ...newTarif, type: e.target.value as any })}>
                      <option value="REMISE_POURCENTAGE">Remise %</option>
                      <option value="PRIX_NET">Prix net</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Valeur</label>
                    <input type="number" className="input-field" value={newTarif.valeur}
                           onChange={(e) => setNewTarif({ ...newTarif, valeur: Number(e.target.value) })} />
                  </div>
                  <button className="btn-primary" onClick={saveTarif}>Enregistrer</button>
                  <button className="btn-outline" onClick={() => setNewTarif(null)}>Annuler</button>
                  <p className="w-full text-xs text-gray-400">S'applique à tout le catalogue de ce client.</p>
                </div>
              ) : (
                <button className="btn-secondary"
                        onClick={() => setNewTarif({ type: 'REMISE_POURCENTAGE', valeur: 0 })}>
                  + Ajouter une condition tarifaire
                </button>
              )}
              {tarifs.length === 0 ? (
                <div className="p-4 text-center text-sm text-gray-500">Aucune condition tarifaire.</div>
              ) : (
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50"><tr>
                    <Th>Portée</Th><Th>Type</Th><Th className="text-right">Valeur</Th><Th></Th>
                  </tr></thead>
                  <tbody className="divide-y divide-gray-100">
                    {tarifs.map((tf) => (
                      <tr key={tf.id}>
                        <Td>{tf.articleDesignation || 'Tous les articles'}</Td>
                        <Td>{tf.type === 'PRIX_NET' ? 'Prix net' : 'Remise %'}</Td>
                        <Td className="text-right">{tf.type === 'PRIX_NET' ? formatTND(tf.valeur) : `${tf.valeur} %`}</Td>
                        <Td className="text-right">
                          <button className="text-red-500 text-xs hover:underline"
                                  onClick={() => removeTarif(tf.id)}>Supprimer</button>
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ) : tab === 'contacts' ? (
            <div className="p-4 space-y-3">
              {newContact ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 border rounded-md p-3 bg-gray-50">
                  <input className="input-field" placeholder="Nom *" value={newContact.nom ?? ''}
                         onChange={(e) => setNewContact({ ...newContact, nom: e.target.value })} />
                  <input className="input-field" placeholder="Fonction" value={newContact.fonction ?? ''}
                         onChange={(e) => setNewContact({ ...newContact, fonction: e.target.value })} />
                  <input className="input-field" placeholder="Email" value={newContact.email ?? ''}
                         onChange={(e) => setNewContact({ ...newContact, email: e.target.value })} />
                  <input className="input-field" placeholder="Téléphone" value={newContact.telephone ?? ''}
                         onChange={(e) => setNewContact({ ...newContact, telephone: e.target.value })} />
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={!!newContact.principal}
                           onChange={(e) => setNewContact({ ...newContact, principal: e.target.checked })} />
                    Contact principal
                  </label>
                  <div className="flex gap-2 justify-end sm:col-span-2">
                    <button className="btn-outline" onClick={() => setNewContact(null)}>Annuler</button>
                    <button className="btn-primary" onClick={saveContact}>Enregistrer</button>
                  </div>
                </div>
              ) : (
                <button className="btn-secondary" onClick={() => setNewContact({})}>+ Ajouter un contact</button>
              )}
              {contacts.length === 0 ? (
                <div className="p-4 text-center text-sm text-gray-500">Aucun contact.</div>
              ) : (
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50"><tr>
                    <Th>Nom</Th><Th>Fonction</Th><Th>Email</Th><Th>Téléphone</Th><Th></Th>
                  </tr></thead>
                  <tbody className="divide-y divide-gray-100">
                    {contacts.map((c) => (
                      <tr key={c.id}>
                        <Td className="font-medium">
                          {c.nom}{c.principal && <span className="ml-2 text-xs text-emerald-600">principal</span>}
                        </Td>
                        <Td>{c.fonction || '—'}</Td>
                        <Td>{c.email || '—'}</Td>
                        <Td>{c.telephone || '—'}</Td>
                        <Td className="text-right">
                          <button className="text-red-500 text-xs hover:underline"
                                  onClick={() => removeContact(c.id)}>Supprimer</button>
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ) : tab === 'opportunites' ? (
            opportunites.length === 0 ? (
              <div className="p-8 text-center text-sm text-gray-500">
                Aucune opportunité. <button className="text-primary-600 underline"
                  onClick={() => navigate('/crm/pipeline')}>Ouvrir le pipeline</button>
              </div>
            ) : (
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50"><tr>
                  <Th>Titre</Th><Th>Étape</Th><Th className="text-right">Montant</Th>
                  <Th className="text-right">Pondéré</Th><Th>Clôture</Th>
                </tr></thead>
                <tbody className="divide-y divide-gray-100">
                  {opportunites.map((o) => (
                    <tr key={o.id}>
                      <Td className="font-medium">{o.titre}</Td>
                      <Td>{ETAPE_OPPORTUNITE_LABELS[o.etape]} · {o.probabilite}%</Td>
                      <Td className="text-right tnd-amount">{formatTND(o.montantEstime)}</Td>
                      <Td className="text-right tnd-amount">{formatTND(o.montantPondere)}</Td>
                      <Td>{o.dateCloturePrev ? formatDate(o.dateCloturePrev) : '—'}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          ) : listByTab.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-500">Aucun élément.</div>
          ) : tab === 'paiements' ? (
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <Th>Date</Th>
                  <Th>Mode</Th>
                  <Th>Référence</Th>
                  <Th className="text-right">Montant</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(listByTab as any[]).map((p: any) => (
                  <tr key={p.id}>
                    <Td>{formatDate(p.datePaiement)}</Td>
                    <Td>{p.modePaiement}</Td>
                    <Td>{p.reference || '—'}</Td>
                    <Td className="text-right tnd-amount font-medium">{formatTND(p.montant)}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <Th>Numéro</Th>
                  <Th>Date</Th>
                  <Th>Échéance</Th>
                  <Th className="text-right">Net</Th>
                  <Th>Statut</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(listByTab as any[]).map((d: any) => (
                  <tr
                    key={d.id}
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => navigate(`/documents/view/${d.id}`)}
                  >
                    <Td className="font-medium text-primary-600">{d.numero}</Td>
                    <Td>{formatDate(d.dateDocument)}</Td>
                    <Td>{d.dateEcheance ? formatDate(d.dateEcheance) : '—'}</Td>
                    <Td className="text-right tnd-amount font-medium">{formatTND(d.netAPayer)}</Td>
                    <Td><StatusBadge statut={d.statutPaiement} /></Td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <ConfirmDialog
        isOpen={showAnon}
        title="Anonymiser ce client (RGPD)"
        message="Les données personnelles du client seront anonymisées de façon irréversible. Les documents historiques sont conservés. Continuer ?"
        onConfirm={anonymize}
        onCancel={() => setShowAnon(false)}
      />
    </div>
  );
}

const ACTIVITE_TYPES: { value: TypeActivite; label: string }[] = [
  { value: 'APPEL', label: 'Appel' },
  { value: 'RDV', label: 'RDV' },
  { value: 'TACHE', label: 'Tâche' },
  { value: 'EMAIL', label: 'E-mail' },
  { value: 'NOTE', label: 'Note' },
];

function ActivitesTab({ clientId, activites, onChange }:
  { clientId: number; activites: Activite[]; onChange: () => void }) {
  const [type, setType] = useState<TypeActivite>('APPEL');
  const [titre, setTitre] = useState('');

  const add = async () => {
    if (!titre.trim()) return;
    await activiteService.create({
      clientId, type, titre: titre.trim(), dateActivite: new Date().toISOString(),
    });
    setTitre('');
    onChange();
  };
  const toggle = async (a: Activite) => { await activiteService.update(a.id, { terminee: !a.terminee }); onChange(); };
  const remove = async (id: number) => { await activiteService.delete(id); onChange(); };

  return (
    <div className="p-4 space-y-4">
      <div className="flex flex-wrap items-end gap-2">
        <select className="input-field" value={type} onChange={(e) => setType(e.target.value as TypeActivite)}>
          {ACTIVITE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <input className="input-field flex-1 min-w-[200px]" value={titre} placeholder="Décrire l'activité…"
          onChange={(e) => setTitre(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') add(); }} />
        <button onClick={add} className="btn-primary">Ajouter</button>
      </div>
      {activites.length === 0 ? (
        <p className="text-gray-500 text-sm">Aucune activité. Consignez vos appels, RDV et tâches ici.</p>
      ) : (
        <ul className="space-y-2">
          {activites.map((a) => (
            <li key={a.id} className="flex items-start gap-3 border-l-2 border-emerald-200 pl-3 py-1">
              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 mt-0.5">
                {ACTIVITE_TYPES.find((t) => t.value === a.type)?.label ?? a.type}
              </span>
              <div className="flex-1">
                <p className={`text-sm ${a.terminee ? 'line-through text-gray-400' : 'text-gray-800'}`}>{a.titre}</p>
                <p className="text-xs text-gray-400">{formatDate(a.dateActivite)}</p>
              </div>
              {(a.type === 'TACHE') && (
                <button onClick={() => toggle(a)} className="text-xs text-emerald-600 hover:underline">
                  {a.terminee ? 'Rouvrir' : 'Terminer'}
                </button>
              )}
              <button onClick={() => remove(a.id)} className="text-xs text-red-500 hover:underline">Suppr.</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

interface KpiProps {
  label: string;
  value: string;
  icon: React.FC<{ className?: string }>;
  accent?: string;
}
const KpiCard: React.FC<KpiProps> = ({ label, value, icon: Icon, accent = 'text-gray-700' }) => (
  <div className="card">
    <div className="flex items-start justify-between">
      <div className="min-w-0">
        <div className="text-xs uppercase tracking-wide text-gray-500 font-medium">{label}</div>
        <div className={`mt-1 text-lg font-semibold truncate ${accent}`}>{value}</div>
      </div>
      <Icon className={`h-5 w-5 ${accent} flex-shrink-0`} />
    </div>
  </div>
);

const Th: React.FC<{ children?: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <th className={`px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase ${className}`}>{children}</th>
);
const Td: React.FC<{ children?: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <td className={`px-4 py-2 text-sm text-gray-700 ${className}`}>{children}</td>
);

export default ClientWorkspace;
